/**
 * CrewDayTimer — the crew's Route Runner.
 *
 * The day, as the crew lives it:
 *  1. Start Route → map appears showing where to go first
 *  2. Drive there — the app watches GPS and auto-starts the job timer on arrival
 *     (manual "I've Arrived" always available; GPS is never required)
 *  3. Work → live timer → Finish (photo + note optional)
 *  4. Map points to the next house; Navigate opens Google Maps turn-by-turn
 *  5. Pause anytime with a reason (gas / lunch / break / other) → Resume
 *  6. Last stop done → Finish Route
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { inWorkingOrder, positionOf } from '../../utils/stopOrder';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { RouteBoard, stopLabel, stopAddress } from '../../components/RouteBoard';
import { api } from '../../services/api';
import { shrinkPhoto } from '../../utils/image';
import { useOfflinePhotoQueue } from '../../hooks/useOfflinePhotoQueue';
import { NotePhotoStrip, NotePhoto } from '../../components/NotePhotos';
import WinterUpsellPrompt from '../../components/crew/WinterUpsellPrompt';
import { businessToday } from '../../utils/time';
import { workLine, WorkJob } from '../../utils/crewWork';
import WorkLine from '../../components/crew/WorkLine';
import { toast } from 'react-toastify';
import {
  Play, Coffee, CheckCircle, Clock, Pause, ChevronRight, X, MapPin,
  Navigation, Camera, SkipForward, Fuel, Sandwich, HelpCircle, ArrowUpDown,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayISO() { return businessToday(); }

/** Parse a server timestamp (bare "YYYY-MM-DD HH:MM:SS", "+00" timestamptz, or ISO) to ms. */
function tsToMs(ts: string | null | undefined): number | null {
  if (!ts) return null;
  let s = String(ts).replace(' ', 'T');
  if (/[+-]\d{2}$/.test(s)) s += ':00';                 // "+00" → "+00:00"
  else if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';   // bare → UTC
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? null : ms;
}

function fmtElapsed(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function fmtMins(mins: number | null | undefined): string {
  if (!mins && mins !== 0) return '—';
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function haversineMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmtDistance(meters: number): string {
  const feet = meters * 3.28084;
  if (feet < 1000) return `${Math.round(feet / 10) * 10} ft`;
  const miles = meters / 1609.34;
  return `${miles.toFixed(miles < 10 ? 1 : 0)} mi`;
}

// The work fields (WorkJob) say what to do at the stop: "Mowing", with
// anything else on the visit as Additional (server stamps work_main /
// work_additional; utils/crewWork).
type Stop = WorkJob & {
  id: number; status: string;
  customer_name: string | null;
  address: string | null; city: string | null; state: string | null;
  property_label: string | null;
  lat: number | null; lng: number | null;
  gate_notes: string | null;
  crew_notes: string | null;
  crew_note_photos?: NotePhoto[] | null;
  time_is_firm?: number | boolean | null;
  is_partial?: number | boolean | null;
  service_tags?: string | null;
  title?: string | null;
  pair_partner_job_id?: number | null;
  pair_partner_label?: string | null;
  pair_note?: string | null;
  estimated_minutes_snapshot: number | null;
  time_start: string | null;
  actual_start_time: string | null;
  completed_at: string | null;
  actual_minutes: number | null;
  photo_count?: number | null;
  position: number;
};

function PairBanner({ stop, stops }: { stop: Stop; stops: Stop[] }) {
  if (!stop.pair_partner_job_id) return null;
  const partner = stops.find(s => s.id === stop.pair_partner_job_id);
  const partnerDone = partner?.status === 'complete';
  return (
    <div className="mt-2.5 bg-indigo-500 rounded-xl px-3.5 py-2.5 shadow-md">
      <p className="text-white font-extrabold text-sm leading-snug">
        🏘 Paired with {stop.pair_partner_label} — {partnerDone ? 'finish this one to close out the street' : 'do both before leaving the street'}
      </p>
      {stop.pair_note && <p className="text-indigo-100 text-xs mt-0.5">{stop.pair_note}</p>}
    </div>
  );
}

function mapsUrl(s: Stop): string {
  const dest = (s.lat != null && s.lng != null)
    ? `${s.lat},${s.lng}`
    : encodeURIComponent([s.address, s.city, s.state].filter(Boolean).join(', '));
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

// ── Bottom sheet ──────────────────────────────────────────────────────────────

function BottomSheet({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const PAUSE_REASONS = [
  { key: 'gas', label: 'Gas', icon: Fuel },
  { key: 'lunch', label: 'Lunch', icon: Sandwich },
  { key: 'break', label: 'Break', icon: Coffee },
  { key: 'other', label: 'Other', icon: HelpCircle },
];

// The owner, Oct 6 2026: "MY crew sees the crossed out jobs fix that". A
// skipped stop is one taken off the day (rain, the customer cancelled this
// week, a yard moved in the re-plan, or the crew skipped it with a reason). It
// is not on the crew's day: never in the list, on the map, in a count or as
// the next stop. The server already leaves it out of everything it sends a
// crew login (server/utils/crewDay); this is the same rule on the phone, so a
// reply cached from before the skip cannot draw it either.
const onTheDay = (s: { status?: string | null }) => s.status !== 'skipped';

// How close (meters) GPS must read before the job auto-starts. Tighter than the
// server's manual-start fence so it can't grab the wrong house.
const AUTO_ARRIVE_METERS = 75;

// ── The crew's reorder (owner, Oct 26 2026: "allow them to change the order of the route") ──
// A stop the crew may move: still to do and never started. Finished, running
// and skipped stops keep their slot — the same rule the server applies
// (services/crewReorder.canMove), so what the phone shows is what gets saved.
const canMoveStop = (s: { status?: string | null; actual_start_time?: string | null }) =>
  (s.status || 'scheduled') === 'scheduled' && !s.actual_start_time;

/** The day with the draft laid in: pinned stops stay in their slot, the draft fills the rest in order. */
function withDraft<T extends { id: number; status: string; actual_start_time: string | null }>(stops: T[], draft: number[]): T[] {
  const byId = new Map(stops.map(s => [s.id, s]));
  let k = 0;
  return stops.map(s => (canMoveStop(s) ? (byId.get(draft[k++]) || s) : s));
}

/** One tap on ▲ or ▼: swap the stop with its neighbour in the draft (the stops still to do, in order). */
function nudge(draft: number[], id: number, dir: -1 | 1): number[] {
  const i = draft.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= draft.length) return draft;
  const next = draft.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CrewDayTimer() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const today = todayISO();

  const [tick, setTick] = useState(0);
  const [finishOpen, setFinishOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [bypassOpen, setBypassOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [lateStartOpen, setLateStartOpen] = useState(false);
  const [bypassMsg, setBypassMsg] = useState('');
  const [bypassReason, setBypassReason] = useState('');
  const [skipReason, setSkipReason] = useState('');
  const [completionNote, setCompletionNote] = useState('');
  const [isPartial, setIsPartial] = useState(false);
  const [partialNote, setPartialNote] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  // The order being changed: the stops still to do, in the order the crew wants. null = not changing it.
  const [draft, setDraft] = useState<number[] | null>(null);

  // ── Live GPS ───────────────────────────────────────────────────────────────
  const [gps, setGps] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [gpsOk, setGpsOk] = useState<boolean | null>(null); // null = unknown yet
  const arriveStreak = useRef(0);
  const autoStartedJob = useRef<number | null>(null);

  // ── Active session ─────────────────────────────────────────────────────────
  const { data: session, refetch: refetchSession } = useQuery(
    ['route-session-active'],
    () => api.get('/route-sessions/active', { params: { date: today } }).then(r => r.data),
    { refetchInterval: 15_000 }
  );
  // A finish photo that cannot go up right now waits on the phone and keeps trying —
  // a bad signal must never stop the crew finishing a stop (CLAUDE.md: offline photo queue).
  const { enqueue: keepPhoto } = useOfflinePhotoQueue({ jobId: String(session?.current_job?.id || ''), onUploaded: () => {} });
  // After Finish: the winter-upsell camera (one tap to deny) — the stop and name it is about.
  const [upsellFor, setUpsellFor] = useState<{ jobId: number; name: string | null } | null>(null);

  // ── Today's routes for the Start screen ────────────────────────────────────
  const { data: todayJobsResp } = useQuery(
    ['crew-jobs-today', today],
    () => api.get('/jobs', { params: { date: today } }).then(r => r.data as any),
    { enabled: !session?.active }
  );

  // GPS watcher — only while a session is running
  useEffect(() => {
    if (!session?.active) { setGps(null); return; }
    if (!('geolocation' in navigator)) { setGpsOk(false); return; }
    const id = navigator.geolocation.watchPosition(
      p => { setGpsOk(true); setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); },
      () => { setGpsOk(false); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [session?.active]);

  // Second-by-second re-render while working
  useEffect(() => {
    if (!session?.active || session?.on_break) { setTick(0); return; }
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [session?.active, session?.on_break]);

  const invalidateAll = () => {
    refetchSession();
    qc.invalidateQueries('crew-all-jobs');
    qc.invalidateQueries('crew-schedule');
    qc.invalidateQueries('crew-jobs-today');
    // The week's pay card sits on this same screen and only reads itself once
    // a minute: after a skip it went on listing the stop as "TO DO" with its
    // pay, and after a finish it lagged the same way. Read it again now.
    qc.invalidateQueries('crew-pay-me');
  };

  // ── Mutations ──────────────────────────────────────────────────────────────

  const startSession = useMutation(
    (routeId: number) => api.post('/route-sessions/start', { route_id: routeId, date: today }),
    {
      onSuccess: () => { toast.success('Route started — head to your first stop 🗺️'); invalidateAll(); },
      // Refused because the office took every stop off since this screen was
      // loaded: read the day again so the button that offered it goes away.
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not start route'); qc.invalidateQueries('crew-jobs-today'); },
    }
  );

  const finishSession = useMutation(
    () => api.post('/route-sessions/finish'),
    {
      onSuccess: () => { toast.success('Route finished! Great work 🎉'); invalidateAll(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not finish route'); },
    }
  );

  const startJob = useMutation(
    (opts: { jobId: number; method: string; bypass?: string; backdate?: number }) =>
      api.post(`/jobs/${opts.jobId}/start`, {
        lat: gps?.lat, lng: gps?.lng,
        start_method: opts.method,
        bypass_reason: opts.bypass || undefined,
        backdate_minutes: opts.backdate || undefined,
      }),
    {
      onSuccess: (_d: any, vars) => {
        const also = _d?.data?.also_started;
        toast.success(also ? `Started ⏱️ — ${also.customer_name}'s next door too (one clock, split at finish)`
          : vars.method === 'auto_geofence' ? "You've arrived — timer started ⏱️"
          : vars.backdate ? `Started — clock set ${vars.backdate} min back ⏱️` : 'Job started ⏱️');
        setBypassOpen(false); setBypassReason(''); setLateStartOpen(false);
        invalidateAll();
      },
      onError: (e: any, vars) => {
        const data = e.response?.data;
        if (data?.can_bypass) {
          // Off-fence or no GPS — let the crew confirm they're on site
          setBypassMsg(data.message || 'Location could not be confirmed.');
          setBypassOpen(true);
        } else if (vars.method !== 'auto_geofence') {
          toast.error(data?.message || 'Could not start job');
        }
        if (vars.method === 'auto_geofence') autoStartedJob.current = null; // allow retry
      },
    }
  );

  const finishJob = useMutation(
    async (jobId: number) => {
      if (photoFile) {
        const file = await shrinkPhoto(photoFile);
        // Offline: kept on the phone. Online but the upload fails: kept on the phone too.
        // Either way the finish goes ahead — the photo follows when the signal does.
        if (!(await keepPhoto(file))) {
          try {
            const form = new FormData();
            form.append('photo', file);
            await api.post(`/jobs/${jobId}/photos`, form);
          } catch {
            if (!(await keepPhoto(file, undefined, true))) toast.warn('Photo did not upload — finishing the stop anyway. Add it from the job page.');
          }
        }
      }
      return api.post(`/jobs/${jobId}/complete`, {
        lat: gps?.lat, lng: gps?.lng,
        completion_note: completionNote.trim() || undefined,
        is_partial: isPartial || undefined,
        partial_note: isPartial ? partialNote.trim() : undefined,
      });
    },
    {
      onSuccess: (_d, jobId) => {
        toast.success(isPartial ? 'Marked partial — office sees what\'s left' : 'Job done! ✅ Next stop is on the map');
        setFinishOpen(false); setCompletionNote(''); setPhotoFile(null); setPhotoPreview(null);
        setIsPartial(false); setPartialNote('');
        invalidateAll();
        // Winter upsell opportunity: one photo of anything that could use work, or "No thanks" (owner, Oct 27 2026).
        setUpsellFor({ jobId, name: session?.current_job?.customer_name || null });
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not complete job'); },
    }
  );

  const adjustStart = useMutation(
    (opts: { jobId: number; minutesAgo: number }) =>
      api.post(`/jobs/${opts.jobId}/adjust-start`, { minutes_ago: opts.minutesAgo }),
    {
      onSuccess: (_d, vars) => {
        toast.success(`Timer adjusted — on site ${vars.minutesAgo} min ⏱️`);
        setAdjustOpen(false);
        invalidateAll();
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not adjust'); },
    }
  );

  const skipJob = useMutation(
    (opts: { jobId: number; reason: string }) =>
      api.post(`/jobs/${opts.jobId}/skip`, { reason: opts.reason }),
    {
      onSuccess: () => {
        // Say where it went: the stop leaves the list the moment the day is
        // read again, and the next one becomes the current stop.
        toast.info("Stop skipped — it's off today's list. The office sees your reason.");
        setSkipOpen(false); setSkipReason('');
        invalidateAll();
        // The morning note above counts today's stops ("4 stops, about 3 h")
        // and is kept for five minutes — read it again, or it goes on
        // counting the stop he just took off.
        qc.invalidateQueries('day-brief');
      },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not skip stop'); },
    }
  );

  const startBreak = useMutation(
    (reason: string) => api.post('/crew-day/break/start', {
      date: today, reason,
      route_id: session?.session?.route_id ?? null,
      lat: gps?.lat ?? null, lng: gps?.lng ?? null,
    }),
    {
      onSuccess: () => { toast.info('Paused ⏸️'); setPauseOpen(false); refetchSession(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not pause'); },
    }
  );

  const endBreak = useMutation(
    () => api.post('/crew-day/break/end', {
      date: today,
      route_id: session?.session?.route_id ?? null,
      lat: gps?.lat ?? null, lng: gps?.lng ?? null,
    }),
    {
      onSuccess: () => { toast.success('Back on the route!'); refetchSession(); },
      onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not resume'); },
    }
  );

  // Save the crew's order: the same route_jobs positions the office writes, for today only; finished and
  // running stops stay put; the day's ETAs are re-figured; the office's Routes page shows who moved what.
  const saveOrder = useMutation(
    (jobIds: number[]) => api.put(`/route-sessions/${session?.session?.id}/order`, { jobIds }),
    {
      onSuccess: (r: any) => {
        toast.success(r?.data?.message || 'Order saved — the office sees it too.');
        setDraft(null);
        invalidateAll();
      },
      onError: (e: any) => {
        const data = e.response?.data;
        toast.error(data?.message || 'Could not save the order');
        // The day changed under them (a stop was taken off or started): the list is read again and they start over.
        if (data?.code === 'STALE' || data?.code === 'SESSION_ENDED' || data?.code === 'NOT_TODAY') { setDraft(null); refetchSession(); }
      },
    }
  );

  // ── Derived state ──────────────────────────────────────────────────────────

  const liveStops: Stop[] = useMemo(() => ((session?.stops || []) as Stop[]).filter(onTheDay), [session?.stops]);
  // While the order is being changed, the map and list show the draft; everything else (current stop, up next,
  // counts) keeps reading the saved day until Save.
  const stops: Stop[] = useMemo(() => (draft ? withDraft(liveStops, draft) : liveStops), [liveStops, draft]);
  const canReorder = !!session?.can_reorder && liveStops.filter(canMoveStop).length > 1;
  const reorderControls = draft ? {
    canMove: (s: { status?: string | null; actual_start_time?: string | null }) => canMoveStop(s),
    onMove: (id: number, dir: -1 | 1) => setDraft(d => (d ? nudge(d, id, dir) : d)),
  } : null;
  const startPin = session?.start ?? null;
  const currentJob: Stop | null = session?.current_job ?? null;
  const working = !!currentJob && currentJob.status === 'in_progress';
  const currentIdx = currentJob ? stops.findIndex(s => s.id === currentJob.id) : -1;
  const nextUp = currentIdx >= 0
    ? stops.slice(currentIdx + 1).filter(s => s.status !== 'complete' && s.status !== 'skipped').slice(0, 2)
    : [];

  const distToStop = (gps && currentJob && currentJob.lat != null && currentJob.lng != null)
    ? haversineMeters(gps.lat, gps.lng, currentJob.lat, currentJob.lng)
    : null;

  // Auto-start on arrival: two consecutive GPS reads inside the radius
  useEffect(() => {
    if (!session?.active || session?.on_break || !currentJob || working) { arriveStreak.current = 0; return; }
    if (distToStop == null || startJob.isLoading) return;
    if (autoStartedJob.current === currentJob.id) return;
    if (distToStop <= AUTO_ARRIVE_METERS && (gps?.accuracy ?? 999) <= 100) {
      arriveStreak.current += 1;
      if (arriveStreak.current >= 2) {
        autoStartedJob.current = currentJob.id;
        startJob.mutate({ jobId: currentJob.id, method: 'auto_geofence' });
      }
    } else {
      arriveStreak.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gps, currentJob?.id, working, session?.active, session?.on_break]);

  // Working timer: the job's own start beats the session-gap window
  const elapsedSecs: number | null = (() => {
    if (!session?.active || session?.on_break || !currentJob) return null;
    const startMs = working
      ? (tsToMs(currentJob.actual_start_time) ?? tsToMs(session?.current_job_started_at))
      : tsToMs(session?.current_job_started_at);
    if (!startMs) return null;
    void tick;
    return Math.floor((Date.now() - startMs) / 1000);
  })();

  const todayRoutes = useMemo(() => {
    const summaries: any[] = todayJobsResp?.route_summaries || [];
    if (summaries.length > 0) {
      return summaries.map((s: any) => ({ id: s.route_id, name: s.route_name || `Route ${s.route_id}` }));
    }
    const jobs: any[] = (todayJobsResp?.jobs || []).filter(onTheDay);
    const seen = new Set<number>();
    const routes: { id: number; name: string }[] = [];
    for (const j of jobs) {
      if (j.route_id && !seen.has(j.route_id)) {
        seen.add(j.route_id);
        routes.push({ id: j.route_id, name: j.route_name || `Route ${j.route_id}` });
      }
    }
    return routes;
  }, [todayJobsResp]);

  const allDone = session?.active && !currentJob && (session?.stops_remaining === 0);
  const jobLabel = stopLabel(currentJob);
  const gpsPoint = gps ? { lat: gps.lat, lng: gps.lng } : null;
  const previewBoards = useMemo(() => {
    const jobs: any[] = todayJobsResp?.jobs || [];
    const by = new Map<number, Stop[]>();
    // Before the day starts: the stops still on it. A route whose stops were
    // all taken off has nothing to draw, and the server does not offer it.
    const sorted = inWorkingOrder(jobs.filter(j => j.route_id && onTheDay(j)));
    for (const j of sorted) {
      const id = Number(j.route_id);
      const stop: Stop = {
        id: j.id, status: j.status, customer_name: j.customer_name,
        address: j.address, city: j.city, state: j.state, property_label: j.property_label,
        lat: j.lat != null ? Number(j.lat) : null, lng: j.lng != null ? Number(j.lng) : null,
        gate_notes: null, crew_notes: null,
        service_type: j.service_type, title: j.title, service_tags: j.service_tags,
        bed_spray: j.bed_spray, spray_needed: j.spray_needed, route_id: j.route_id,
        recurring_schedule_id: j.recurring_schedule_id,
        work_main: j.work_main, work_additional: j.work_additional,
        estimated_minutes_snapshot: j.estimated_minutes_snapshot, time_start: j.time_start,
        actual_start_time: null, completed_at: null, actual_minutes: null,
        position: positionOf(j),
      };
      const list = by.get(id) || [];
      list.push(stop);
      by.set(id, list);
    }
    return Array.from(by.entries()).map(([id, stops]) => ({ id, stops }));
  }, [todayJobsResp]);

  // ── 1. Not started ─────────────────────────────────────────────────────────
  if (!session?.active) {
    return (
      <div className="space-y-3">
        {previewBoards.map(g => <RouteBoard key={g.id} stops={g.stops} currentId={null} gps={null} start={session?.starts?.[g.id] || startPin} />)}
        <div className="bg-brand-600 rounded-2xl p-5 shadow-lg">
        <p className="text-brand-200 text-xs font-semibold uppercase tracking-wide mb-1">Ready to work?</p>
        <p className="text-white text-lg font-bold mb-4">Start your route to begin</p>
        {todayRoutes.length === 0 ? (
          <p className="text-brand-200 text-sm">No route assigned for today.</p>
        ) : todayRoutes.length === 1 ? (
          <button
            onClick={() => startSession.mutate(todayRoutes[0].id)}
            disabled={startSession.isLoading}
            className="w-full py-3.5 bg-white text-brand-700 font-bold rounded-xl text-base flex items-center justify-center gap-2 active:bg-brand-50 disabled:opacity-60"
          >
            {startSession.isLoading
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600" /> Starting…</>
              : <><Play className="h-5 w-5" /> Start Route — {todayRoutes[0].name}</>}
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-brand-200 text-xs">Pick your route for today:</p>
            {todayRoutes.map(r => (
              <button key={r.id}
                onClick={() => startSession.mutate(r.id)}
                disabled={startSession.isLoading}
                className="w-full py-3 bg-white text-brand-700 font-bold rounded-xl text-sm flex items-center justify-center gap-2 active:bg-brand-50 disabled:opacity-60"
              >
                <Play className="h-4 w-4" /> {r.name}
              </button>
            ))}
          </div>
        )}
        </div>
      </div>
    );
  }

  // ── 2. Paused ──────────────────────────────────────────────────────────────
  if (session?.on_break) {
    const reasonLabel = PAUSE_REASONS.find(r => r.key === session.break_reason)?.label || 'Break';
    return (
      <div className="space-y-3">
        <RouteBoard stops={stops} currentId={currentJob?.id ?? null} gps={gpsPoint} start={startPin} />
        <div className="bg-amber-500 rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <Pause className="h-4 w-4 text-amber-100" />
          <p className="text-amber-100 text-xs font-semibold uppercase tracking-wide">Paused — {reasonLabel}</p>
        </div>
        <p className="text-white text-sm mb-1">
          {session.stops_done} of {session.stops_total} stops done · {fmtMins(session.elapsed_minutes)} worked
        </p>
        <p className="text-amber-100 text-xs">Pause time doesn't count against your jobs.</p>
        <button
          onClick={() => endBreak.mutate()}
          disabled={endBreak.isLoading}
          className="mt-3 w-full py-3.5 bg-white text-amber-700 font-bold rounded-xl text-base flex items-center justify-center gap-2 active:bg-amber-50 disabled:opacity-60"
        >
          {endBreak.isLoading
            ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600" /> Resuming…</>
            : <><Play className="h-5 w-5" /> Back to the Route</>}
        </button>
        </div>
      </div>
    );
  }

  // ── 3. All stops done ──────────────────────────────────────────────────────
  if (allDone) {
    // Every stop was taken off after the route was started (the office called
    // the day for rain, or the crew skipped the only one): nothing was done
    // and nothing is left. "0 stops complete" under "All stops done!" reads
    // like a mistake — say what happened, and let them finish the day.
    const nothingLeft = session.stops_total === 0;
    return (
      <div className="space-y-3">
        <RouteBoard stops={stops} currentId={null} gps={gpsPoint} start={startPin} />
        <div className="bg-green-600 rounded-2xl p-5 shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle className="h-5 w-5 text-green-200" />
          <p className="text-green-100 text-xs font-semibold uppercase tracking-wide">{nothingLeft ? 'Nothing left today' : 'All stops done!'}</p>
        </div>
        <p className="text-white text-lg font-bold mb-1">
          {nothingLeft
            ? "No stops left on today's list"
            : `${session.stops_done} stop${session.stops_done !== 1 ? 's' : ''} complete`}
        </p>
        <p className="text-green-200 text-sm mb-4">Working time: {fmtMins(session.elapsed_minutes)}</p>
        <button
          onClick={() => finishSession.mutate()}
          disabled={finishSession.isLoading}
          className="w-full py-3.5 bg-white text-green-700 font-bold rounded-xl text-base flex items-center justify-center gap-2 active:bg-green-50 disabled:opacity-60"
        >
          {finishSession.isLoading
            ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" /> Finishing…</>
            : <><CheckCircle className="h-5 w-5" /> Finish Route</>}
        </button>
        </div>
      </div>
    );
  }

  // ── 4. On the route: navigating or working ─────────────────────────────────
  return (
    <>
      <div className="space-y-3">
        <RouteBoard stops={stops} currentId={currentJob?.id ?? null} gps={gpsPoint} start={startPin} reorder={reorderControls} />
        {/* Change the order of the stops still to do (owner, Oct 26 2026). One big button to start; while
            changing, ▲▼ on each stop and a Save / Cancel bar. Finished and running stops stay where they are. */}
        {draft ? (
          <div className="bg-white rounded-2xl p-3 shadow-sm border border-amber-200 space-y-2">
            <p className="text-xs text-gray-600 text-center">Tap ▲ ▼ to move a stop. Stops already done or started stay put.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setDraft(null)} disabled={saveOrder.isLoading}
                className="flex-1 py-3.5 rounded-xl border-2 border-gray-300 text-gray-700 font-bold text-base">Cancel</button>
              <button type="button" onClick={() => saveOrder.mutate(draft)}
                disabled={saveOrder.isLoading || draft.every((id, i) => liveStops.filter(canMoveStop)[i]?.id === id)}
                className="flex-1 py-3.5 rounded-xl bg-green-600 active:bg-green-700 text-white font-bold text-base flex items-center justify-center gap-2 disabled:opacity-50">
                {saveOrder.isLoading
                  ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Saving…</>
                  : <><CheckCircle className="h-5 w-5" /> Save order</>}
              </button>
            </div>
          </div>
        ) : canReorder ? (
          <button type="button" onClick={() => setDraft(liveStops.filter(canMoveStop).map(s => s.id))}
            className="w-full py-3 rounded-2xl bg-white border border-gray-200 shadow-sm text-gray-800 font-semibold text-sm flex items-center justify-center gap-2 active:bg-gray-50">
            <ArrowUpDown className="h-4 w-4" /> Change the order of the stops
          </button>
        ) : null}
        <div className="bg-brand-700 rounded-2xl overflow-hidden shadow-lg">
        <div className="px-4 pt-3 flex items-center justify-between gap-2">
          <span className="text-white text-xs font-mono font-semibold flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-brand-200" />
            Stop {session.stops_done + 1}/{session.stops_total} · {fmtMins(session.elapsed_minutes)}
          </span>
          {gpsOk === false && <span className="text-brand-200 text-[11px]">GPS off</span>}
        </div>

        <div className="p-4">
          {working ? (
            /* ── WORKING ── */
            <>
              <div className="flex items-center justify-between mb-1">
                <p className="text-green-300 text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
                  </span>
                  Working
                </p>
                {currentJob?.estimated_minutes_snapshot && (
                  <span className="text-brand-300 text-xs">~{currentJob.estimated_minutes_snapshot} min est.</span>
                )}
              </div>
              <p className="text-white text-xl font-bold leading-tight mb-0.5">{jobLabel}</p>
              <WorkLine job={currentJob} tone="dark" size="md" className="mb-1" />
              {stopAddress(currentJob) && <p className="text-brand-300 text-sm mb-1.5">{stopAddress(currentJob)}</p>}
              {elapsedSecs != null && (
                <div className="flex items-baseline gap-2.5 mb-3">
                  <p className="text-white text-3xl font-mono font-black tracking-tight">{fmtElapsed(elapsedSecs)}</p>
                  {working && (
                    <button onClick={() => setAdjustOpen(true)}
                      className="text-brand-300 text-xs underline decoration-dotted underline-offset-2">
                      adjust
                    </button>
                  )}
                </div>
              )}
              {currentJob && (currentJob.crew_notes || (currentJob.crew_note_photos || []).length > 0) && (
                <div className="mb-3 bg-amber-400 rounded-xl px-3.5 py-2.5 shadow-md">
                  {currentJob.crew_notes && <p className="text-amber-950 font-extrabold text-sm leading-snug">{currentJob.crew_notes}</p>}
                  {(currentJob.crew_note_photos || []).length > 0 && <div className={currentJob.crew_notes ? 'mt-2' : ''}><NotePhotoStrip photos={currentJob.crew_note_photos} size="sm" /></div>}
                </div>
              )}
              {currentJob && <div className="mb-3 -mt-1"><PairBanner stop={currentJob} stops={stops} /></div>}
              {currentJob?.gate_notes && (
                <p className="text-brand-200 text-xs mb-3">🔑 {currentJob.gate_notes}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => setFinishOpen(true)}
                  className="flex-1 py-3.5 bg-green-500 active:bg-green-600 text-white font-bold rounded-xl text-base flex items-center justify-center gap-2"
                >
                  <CheckCircle className="h-5 w-5" /> Finish
                </button>
                <button
                  onClick={() => setPauseOpen(true)}
                  className="px-4 py-3.5 bg-amber-500 active:bg-amber-600 text-white font-semibold rounded-xl flex items-center gap-1.5"
                >
                  <Pause className="h-5 w-5" />
                </button>
                {currentJob && (
                  <button
                    onClick={() => navigate(`/crew/jobs/${currentJob.id}`)}
                    className="press px-4 py-3.5 bg-brand-500 active:bg-brand-600 text-black font-semibold rounded-xl flex items-center"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
              </div>
            </>
          ) : (
            /* ── NAVIGATING ── */
            <>
              <div className="flex items-center justify-between mb-1">
                <p className="text-brand-200 text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> Head to
                </p>
                {distToStop != null && (
                  <span className="press text-black text-xs font-mono bg-brand-600 rounded-full px-2.5 py-0.5">
                    {fmtDistance(distToStop)} away
                  </span>
                )}
              </div>
              <p className="text-white text-xl font-bold leading-tight">{jobLabel}</p>
              <WorkLine job={currentJob} tone="dark" size="md" className="mt-0.5" />
              {currentJob?.address && (
                <p className="text-brand-300 text-sm mt-0.5">
                  {stopAddress(currentJob)}{currentJob.city ? `, ${currentJob.city}` : ''}
                </p>
              )}
              {!!currentJob?.time_is_firm && currentJob?.time_start && (
                <p className="mt-1.5 inline-block text-white bg-red-600 font-bold text-xs px-2.5 py-1 rounded-full">
                  ⏰ MUST START {currentJob.time_start}
                </p>
              )}
              {currentJob && (currentJob.crew_notes || (currentJob.crew_note_photos || []).length > 0) && (
                <div className="mt-2.5 bg-amber-400 rounded-xl px-3.5 py-2.5 shadow-md">
                  {currentJob.crew_notes && <p className="text-amber-950 font-extrabold text-sm leading-snug">{currentJob.crew_notes}</p>}
                  {(currentJob.crew_note_photos || []).length > 0 && <div className={currentJob.crew_notes ? 'mt-2' : ''}><NotePhotoStrip photos={currentJob.crew_note_photos} size="sm" /></div>}
                </div>
              )}
              {currentJob && <PairBanner stop={currentJob} stops={stops} />}
              {gpsOk && distToStop != null && distToStop <= AUTO_ARRIVE_METERS && (
                <p className="text-green-300 text-xs mt-1">Arriving — timer will start automatically…</p>
              )}
              <div className="flex gap-2 mt-4">
                {currentJob && (
                  <a href={mapsUrl(currentJob)} target="_blank" rel="noopener noreferrer"
                    className="flex-1 py-3.5 bg-blue-500 active:bg-blue-600 text-white font-bold rounded-xl text-base flex items-center justify-center gap-2"
                  >
                    <Navigation className="h-5 w-5" /> Navigate
                  </a>
                )}
                <button
                  onClick={() => currentJob && startJob.mutate({ jobId: currentJob.id, method: 'geofence_prompt' })}
                  disabled={startJob.isLoading}
                  className="flex-1 py-3.5 bg-green-500 active:bg-green-600 text-white font-bold rounded-xl text-base flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {startJob.isLoading
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    : <><Play className="h-5 w-5" /> I've Arrived</>}
                </button>
              </div>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => setPauseOpen(true)}
                  className="flex-1 py-2.5 bg-brand-600 active:bg-brand-500 text-brand-100 font-medium rounded-xl text-sm flex items-center justify-center gap-1.5"
                >
                  <Pause className="h-4 w-4" /> Pause
                </button>
                <button
                  onClick={() => setSkipOpen(true)}
                  className="flex-1 py-2.5 bg-brand-600 active:bg-brand-500 text-brand-100 font-medium rounded-xl text-sm flex items-center justify-center gap-1.5"
                >
                  <SkipForward className="h-4 w-4" /> Skip Stop
                </button>
              </div>
              <button onClick={() => setLateStartOpen(true)}
                className="mt-2 w-full text-center text-brand-300 text-xs underline decoration-dotted underline-offset-2">
                Already been here a while? Start with time credit
              </button>
            </>
          )}

          {/* Up next */}
          {nextUp.length > 0 && (
            <div className="mt-4 pt-3 border-t border-brand-600">
              <p className="text-brand-300 text-[11px] font-semibold uppercase tracking-wide mb-1.5">Up next</p>
              {nextUp.map(s => (
                <div key={s.id} className="mb-1">
                  <p className="text-brand-200 text-xs truncate">
                    {stops.findIndex(x => x.id === s.id) + 1}. {stopLabel(s)}
                    {s.time_is_firm && s.time_start ? <span className="text-red-300 font-bold"> · ⏰ {s.time_start}</span> : ''}
                    {stopAddress(s) ? <span className="text-brand-300"> · {stopAddress(s)}</span> : ''}
                    {s.estimated_minutes_snapshot ? ` · ~${s.estimated_minutes_snapshot}m` : ''}
                  </p>
                  {/* What to do there, on its own line so it never pushes the must-start time off the card */}
                  <p className="text-white text-xs font-semibold leading-snug pl-3.5">{workLine(s)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* ── Finish sheet: note + optional photo ── */}
      {upsellFor && (
        <WinterUpsellPrompt jobId={upsellFor.jobId} customerName={upsellFor.name} onClose={() => setUpsellFor(null)}
          enqueueOffline={(file, opts, force) => keepPhoto(file, opts, force)} />
      )}
      {finishOpen && currentJob && (
        <BottomSheet title={`Finish: ${jobLabel}`} onClose={() => { setFinishOpen(false); }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Photo <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              {photoPreview ? (
                <div className="relative inline-block">
                  <img src={photoPreview} alt="Job" className="h-24 w-24 object-cover rounded-xl" />
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    className="absolute -top-2 -right-2 bg-gray-900 text-white rounded-full p-1"
                  ><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 text-sm font-medium cursor-pointer active:bg-gray-50">
                  <Camera className="h-5 w-5" /> Take Photo
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) { setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f)); }
                    }} />
                </label>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Note <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <textarea
                value={completionNote}
                onChange={e => setCompletionNote(e.target.value)}
                rows={2}
                placeholder="Anything the office should know…"
                className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <label className={`flex items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer ${isPartial ? 'bg-amber-50 border-amber-300' : 'border-gray-200'}`}>
              <input type="checkbox" checked={isPartial} onChange={e => setIsPartial(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-amber-600" />
              <span className="text-sm">
                <span className="font-semibold text-gray-800">Only partially done</span>
                <span className="block text-xs text-gray-500">Tell the office what's left</span>
              </span>
            </label>
            {isPartial && (
              <textarea value={partialNote} onChange={e => setPartialNote(e.target.value)} rows={2}
                placeholder="What still needs doing? (required)"
                className="w-full border border-amber-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setFinishOpen(false)}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm"
              >Cancel</button>
              <button
                onClick={() => finishJob.mutate(currentJob.id)}
                disabled={finishJob.isLoading || (isPartial && !partialNote.trim())}
                className="flex-1 py-3 rounded-xl bg-green-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {finishJob.isLoading
                  ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Saving…</>
                  : <><CheckCircle className="h-4 w-4" /> Done — Next Stop</>}
              </button>
            </div>
          </div>
        </BottomSheet>
      )}

      {/* ── Pause sheet: why? ── */}
      {pauseOpen && (
        <BottomSheet title="Pause the route — why?" onClose={() => setPauseOpen(false)}>
          <div className="grid grid-cols-2 gap-2">
            {PAUSE_REASONS.map(r => (
              <button key={r.key}
                onClick={() => startBreak.mutate(r.key)}
                disabled={startBreak.isLoading}
                className="py-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 font-bold text-sm flex flex-col items-center gap-1.5 active:bg-amber-100 disabled:opacity-50"
              >
                <r.icon className="h-6 w-6" /> {r.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center">Pause time won't count against your work hours.</p>
        </BottomSheet>
      )}

      {/* ── Skip sheet ── */}
      {skipOpen && currentJob && (
        <BottomSheet title={`Skip: ${jobLabel}?`} onClose={() => { setSkipOpen(false); setSkipReason(''); }}>
          <div className="space-y-4">
            <textarea
              value={skipReason}
              onChange={e => setSkipReason(e.target.value)}
              rows={2} autoFocus
              placeholder="Why are you skipping? (gate locked, dog out, already mowed…)"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={() => skipJob.mutate({ jobId: currentJob.id, reason: skipReason.trim() })}
              disabled={!skipReason.trim() || skipJob.isLoading}
              className="w-full py-3 rounded-xl bg-gray-800 text-white font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <SkipForward className="h-4 w-4" /> Skip This Stop
            </button>
          </div>
        </BottomSheet>
      )}

      {/* ── Adjust running timer ── */}
      {adjustOpen && currentJob && (
        <BottomSheet title="How long have you been on site?" onClose={() => setAdjustOpen(false)}>
          <div className="grid grid-cols-4 gap-2">
            {[5, 10, 15, 20, 30, 45, 60, 90].map(m => (
              <button key={m}
                onClick={() => adjustStart.mutate({ jobId: currentJob.id, minutesAgo: m })}
                disabled={adjustStart.isLoading}
                className="py-3 rounded-xl bg-brand-50 border border-brand-200 text-brand-800 font-bold text-sm active:bg-brand-100 disabled:opacity-50">
                {m}m
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center">Sets the timer to that many minutes ago.</p>
        </BottomSheet>
      )}

      {/* ── Late start: begin with time already on site ── */}
      {lateStartOpen && currentJob && (
        <BottomSheet title="Start the job — been here how long?" onClose={() => setLateStartOpen(false)}>
          <div className="grid grid-cols-4 gap-2">
            {[5, 10, 15, 20, 30, 45, 60, 90].map(m => (
              <button key={m}
                onClick={() => startJob.mutate({ jobId: currentJob.id, method: 'geofence_prompt', backdate: m })}
                disabled={startJob.isLoading}
                className="py-3 rounded-xl bg-green-50 border border-green-200 text-green-800 font-bold text-sm active:bg-green-100 disabled:opacity-50">
                {m}m
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 text-center">The timer starts already showing that much time.</p>
        </BottomSheet>
      )}

      {/* ── Geofence bypass sheet ── */}
      {bypassOpen && currentJob && (
        <BottomSheet title="Can't confirm your location" onClose={() => { setBypassOpen(false); setBypassReason(''); }}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">{bypassMsg}</p>
            <textarea
              value={bypassReason}
              onChange={e => setBypassReason(e.target.value)}
              rows={2}
              placeholder="Quick note (e.g. GPS acting up, I'm at the property)"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={() => startJob.mutate({
                jobId: currentJob.id, method: 'geofence_prompt',
                bypass: bypassReason.trim() || 'GPS unavailable — crew confirmed on site',
              })}
              disabled={startJob.isLoading}
              className="w-full py-3 rounded-xl bg-green-600 text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Play className="h-4 w-4" /> Start Anyway
            </button>
          </div>
        </BottomSheet>
      )}
    </>
  );
}
